import _ from 'lodash'

import VALUES from 'core/data-model/constants/values'
import { calculatePeriod, calculateWaivePeriod } from 'core/service/basic-plan/period'
import { isValidConfigurationForTMO } from 'core/service/basic-plan/validation'
import { isRequirePayerInfo } from 'core/service/rider/rider'

export const _isUsedTMORate = (riderPremiumRatesPayload, riders) => {
  for (let premium of riderPremiumRatesPayload) {
    for (let rider of riders) {
      if (rider === premium.riderCode) {
        let effectiveDate = _.get(premium, 'effectiveDate')
        if (isValidConfigurationForTMO(rider, effectiveDate, _.get(premium, 'expiryDate'))) {
          return rider === VALUES.PLS
            ? effectiveDate === VALUES.PLS_EFFECTIVE_DATE
            : effectiveDate === VALUES.TMO_EFFECTIVE_DATE
        }
        break
      }
    }
  }
  return false
}

export const findListRiderPlanCodeSelectedRiderPlanGroup = (rider) => {
  const riderSelectedPlan = _.get(rider, 'selectedPlan.planCode')
  const packageRiders = _.get(rider, 'packageRiders')
  const matchPlanGroups = packageRiders.find((plan) => plan.planGroupCode === riderSelectedPlan)
  return matchPlanGroups ? matchPlanGroups.planGroups.map((plan) => plan.code) : []
}

export const findSelectedRiderPlanGroup = (rider) => {
  const riderSelectedPlan = _.get(rider, 'selectedPlan.planCode')
  const packageRiders = _.get(rider, 'packageRiders')
  return packageRiders.find((plan) => plan.planGroupCode === riderSelectedPlan)
}

/**
 * createRiderPremiumRateService
 * @param dataWrapper {ReturnType<import('core/data/data-wrapper')._DB>}
 * @param displayProductService {ReturnType<import('core/service/display-product/display-product').createDisplayProductService>}
 * @param insuredOccupationService {ReturnType<import('core/service/insured/occupation').createInsuredOccupationService>}
 * @param riderService {Awaited<ReturnType<import('core/service/rider/rider').createRiderService>>}
 */
export function createRiderPremiumRateService(
  dataWrapper,
  displayProductService,
  insuredOccupationService,
  riderService
) {
  const _getRiderPremiumRates = (
    age,
    payerAge,
    gender,
    payerGender,
    riders,
    basicPlanCode,
    occupationFactor,
    riderPremiumRatesPayload,
    basicYearsOfPayment
  ) => {
    const riderPremiumRates = riders.map((rider) => {
      let riderPremiumRates

      const planCode = rider.selectedPlan ? rider.selectedPlan.planCode : ''

      for (let premium of riderPremiumRatesPayload) {
        if ([rider.code, planCode].includes(premium.riderCode)) {
          const effectiveDate = _.get(premium, 'effectiveDate', '')
          const expiryDate = _.get(premium, 'expiryDate', '')

          if (_.isEmpty(effectiveDate) && _.isEmpty(expiryDate)) {
            riderPremiumRates = premium
            break
          } else {
            if (isValidConfigurationForTMO(rider.code, effectiveDate, expiryDate)) {
              riderPremiumRates = premium
              break
            }
          }
        }
      }
      if (!riderPremiumRates) return { code: rider.code, premiumRate: 0 }

      const queryDependencies = {
        selectedBasicPlanCode: basicPlanCode,
        selectedRiderPlanCode: planCode,
        gender: isRequirePayerInfo(rider.code) ? payerGender : gender,
        age: isRequirePayerInfo(rider.code) ? payerAge : age,
        yearsOfCoverage: rider.yearsOfCoverage,
        occupationFactor: occupationFactor[rider.code],
        riderSumAssured: rider.sumAssured,
        basicYearsOfPayment: basicYearsOfPayment,
        waivePeriod: calculateWaivePeriod(rider.code, basicYearsOfPayment, age.unit !== 'year' ? 0 : age.value),
      }

      const premiumRate = riderService.queryPremiumRate(rider, riderPremiumRates, queryDependencies)
      return { code: rider.code, premiumRate: premiumRate }
    })

    // TODO: Test this once module requirement is clear
    const riderModulePremiumRate = _(riders)
      .filter('selectedPlan.modules')
      .value()
      .map((rider) => {
        const queryDependencies = {
          selectedBasicPlanCode: basicPlanCode,
          selectedRiderPlanCode: rider.selectedPlan ? rider.selectedPlan.planCode : '',
          gender: isRequirePayerInfo(rider.code) ? payerGender : gender,
          age: isRequirePayerInfo(rider.code) ? payerAge : age,
          yearsOfCoverage: rider.yearsOfCoverage,
          occupationFactor: occupationFactor[rider.code],
          riderSumAssured: rider.sumAssured,
        }

        return rider.selectedPlan.modules.map((riderModule) => {
          const riderPremiumRates = _.find(riderPremiumRatesPayload, { riderCode: riderModule.code })
          if (!riderPremiumRates) return { code: riderModule.code, premiumRate: 0 }

          const premiumRate = riderService.queryPremiumRate(rider, riderPremiumRates, queryDependencies)
          return { code: riderModule.code, premiumRate: premiumRate }
        })
      })
      .reduce((flatten, premium) => flatten.concat(premium), [])

    const riderAnyValuePlanGroupPremiumRate = _(riders)
      .filter((rider) => _.get(rider, 'options[0].type') === VALUES.OPTION_ANY_VALUE_PLAN_GROUP)
      .value()
      .map((rider) => {
        const riderPlanCodes = findListRiderPlanCodeSelectedRiderPlanGroup(rider)
        return riderPlanCodes.map((riderPlanCode) => {
          const riderPremiumRates = _.find(riderPremiumRatesPayload, { riderCode: rider.code })
          if (!riderPremiumRates) return { code: riderPlanCode, premiumRate: 0 }

          const queryDependencies = {
            selectedBasicPlanCode: basicPlanCode,
            selectedRiderPlanCode: riderPlanCode ? riderPlanCode : '',
            gender: isRequirePayerInfo(rider.code) ? payerGender : gender,
            age: isRequirePayerInfo(rider.code) ? payerAge : age,
            yearsOfCoverage: rider.yearsOfCoverage,
            occupationFactor: occupationFactor[rider.code],
            riderSumAssured: rider.sumAssured,
          }

          const premiumRate = riderService.queryPremiumRate(rider, riderPremiumRates, queryDependencies)
          return { code: riderPlanCode, premiumRate: premiumRate }
        })
      })
      .reduce((flatten, premium) => flatten.concat(premium), [])

    const result = _(riderPremiumRates)
      .union(riderModulePremiumRate)
      .union(riderAnyValuePlanGroupPremiumRate)
      .keyBy('code')
      .mapValues((riderPremiumRate) => riderPremiumRate.premiumRate)
      .value()

    return result
  }

  const getRiderPremiumRates = async (
    age,
    payerAge,
    gender,
    payerGender,
    riders,
    natureOfDuties,
    displayProductQuery
  ) => {
    const basicPlan = await displayProductService.getBasicPlan(displayProductQuery)
    const riderPremiumRatesPayload = await dataWrapper.getRiderPremiumRates()
    const occupationFactor = await insuredOccupationService.getRiskiestOccupationFactorForRiders(
      displayProductQuery,
      natureOfDuties,
      riders
    )
    const basicYearsOfPayment = calculatePeriod(basicPlan.paymentPeriod, age.unit !== 'year' ? 0 : age.value)
    return _getRiderPremiumRates(
      age,
      payerAge,
      gender,
      payerGender,
      riders,
      basicPlan.productCode,
      occupationFactor,
      riderPremiumRatesPayload,
      basicYearsOfPayment
    )
  }

  const calculatePremiumOptionPlanPA = async (rider, riderOccupationFactor, planGroupPA, insuredAge) => {
    const riderPremiumRatesPayload = await dataWrapper.getRiderPremiumRates()
    const riderPremiumRates = _.find(riderPremiumRatesPayload, { riderCode: rider.code })

    return planGroupPA.map((riderOption) => {
      if (!riderPremiumRates) return { code: riderOption.planCode, premiumRate: 0 }

      const queryDependencies = {
        selectedBasicPlanCode: '',
        selectedRiderPlanCode: riderOption.planCode,
        gender: '',
        age: insuredAge,
        yearsOfCoverage: 0,
        occupationFactor: riderOccupationFactor,
        riderSumAssured: 0,
      }

      const premiumRate = riderService.queryPremiumRate(rider, riderPremiumRates, queryDependencies)
      return { code: riderOption.planCode, premiumRate: premiumRate }
    })
  }

  const isUsedTMORate = async () => {
    return _isUsedTMORate(await dataWrapper.getRiderPremiumRates(), [VALUES.DCI])
  }

  return {
    _getRiderPremiumRates,
    getRiderPremiumRates,
    calculatePremiumOptionPlanPA,
    isUsedTMORate,
    findListRiderPlanCodeSelectedRiderPlanGroup,
    findSelectedRiderPlanGroup,
  }
}
